import { useMemo } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import type { TicketFilterOptions } from '@/types/support';
import type { TicketScopeParams } from '../api/support.api';

export interface TicketScopeFiltersProps {
  value: TicketScopeParams;
  onChange: (next: TicketScopeParams) => void;
  options: TicketFilterOptions | null;
}

/**
 * Which part of the ticket book to look at: by state, by school, by the person
 * who raised it. The date range sits beside the table it narrows.
 *
 * Narrowing cascades — a state limits the schools offered, a school limits the
 * people — and widening clears what no longer fits, so the bar can never hold
 * a school from one state under a filter for another.
 */
export function TicketScopeFilters({ value, onChange, options }: TicketScopeFiltersProps) {
  const institutes = useMemo(
    () => (options?.institutes ?? []).filter((institute) => !value.state || institute.state === value.state),
    [options, value.state],
  );

  const raisers = useMemo(() => {
    const allowed = value.institute
      ? new Set([value.institute])
      : value.state
        ? new Set(institutes.map((institute) => institute.uuid))
        : null;
    return (options?.raisers ?? []).filter(
      (person) => !allowed || (person.instituteUuid !== null && allowed.has(person.instituteUuid)),
    );
  }, [options, value.institute, value.state, institutes]);

  const hasAny = Boolean(value.state || value.institute || value.raisedBy);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Select
        label="State"
        options={[{ value: '', label: 'Every state' }, ...(options?.states ?? []).map((state) => ({ value: state, label: state }))]}
        value={value.state ?? ''}
        disabled={!options}
        onChange={(event) => {
          const state = event.target.value || undefined;
          const keepsSchool = value.institute && options?.institutes.some(
            (institute) => institute.uuid === value.institute && (!state || institute.state === state),
          );
          onChange({
            ...value,
            state,
            institute: keepsSchool ? value.institute : undefined,
            raisedBy: keepsSchool ? value.raisedBy : undefined,
          });
        }}
      />
      <Select
        label="School"
        options={[
          { value: '', label: value.state ? `Every school in ${value.state}` : 'Every school' },
          ...institutes.map((institute) => ({ value: institute.uuid, label: institute.displayName })),
        ]}
        value={value.institute ?? ''}
        disabled={!options}
        onChange={(event) => {
          const institute = event.target.value || undefined;
          const raiser = options?.raisers.find((person) => person.uuid === value.raisedBy);
          onChange({
            ...value,
            institute,
            raisedBy: institute && raiser?.instituteUuid !== institute ? undefined : value.raisedBy,
          });
        }}
      />
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Select
            label="Raised by"
            options={[
              { value: '', label: 'Anyone' },
              ...raisers.map((person) => ({ value: person.uuid, label: person.name })),
            ]}
            value={value.raisedBy ?? ''}
            disabled={!options}
            onChange={(event) => onChange({ ...value, raisedBy: event.target.value || undefined })}
          />
        </div>
        {hasAny && (
          <Button
            variant="secondary"
            iconOnly
            aria-label="Clear the state, school and person filters"
            title="Clear filters"
            // The date range lives beside the table now and is cleared there.
            onClick={() => onChange({ search: value.search, from: value.from, to: value.to })}
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        )}
      </div>
    </div>
  );
}
